/**
 * Department Hooks - React Query hooks for department operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userService } from '../services';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../utils/apiError';

// Query keys
export const departmentKeys = {
  all: ['departments'] as const,
  list: () => [...departmentKeys.all, 'list'] as const,
};

export interface Department {
  id: string;
  name: string;
  userCount: number;
}

/**
 * Get all departments
 */
export const useDepartments = () => {
  return useQuery({
    queryKey: departmentKeys.list(),
    queryFn: () => userService.getDepartments(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Create department
 */
export const useCreateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => userService.createDepartment(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
      toast.success('Department created successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create department'));
    },
  });
};

/**
 * Update department
 */
export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ oldName, newName }: { oldName: string; newName: string }) =>
      userService.updateDepartment(oldName, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
      toast.success('Department updated successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update department'));
    },
  });
};

/**
 * Delete department
 */
export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => userService.deleteDepartment(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: departmentKeys.list() });
      toast.success('Department deleted successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete department'));
    },
  });
};
